import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { User, Eye, EyeOff, Lock } from 'lucide-react'
import {
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Checkbox } from 'components/ui/checkbox'
import { toast } from 'sonner'
import { auth, db } from '../../../firebase'

export default function LoginPage() {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [rememberMe, setRememberMe] = React.useState(true)
  const userCollectionName = 'users'
  const allowedUserTypes = [
    'Vice Chancellor',
    'Admin',
    'Accountant',
    'Project Member',
    'Principal Investigator',
    'DVC ASAR',
    'System Admin',
    'Grant Accountant',
    'Director Drie',
    'DVC PAF',
    'Project Member Research',
    'Project Member ICT',
    'Co-Principal Investigator',
    'Project Manager',
  ]

  function getAuthErrorMessage(error) {
    const code = error?.code || ''
    if (code === 'auth/invalid-email') return 'Please enter a valid email address.'
    if (code === 'auth/user-disabled') return 'This account has been disabled.'
    if (code === 'auth/user-not-found') return 'No account found with that email.'
    if (code === 'auth/wrong-password') return 'Incorrect password. Please try again.'
    if (code === 'auth/invalid-credential') return 'Invalid credentials. Please try again.'
    if (code === 'auth/too-many-requests') {
      return 'Too many attempts. Please wait a moment and try again.'
    }
    return 'Unable to sign in right now. Please try again.'
  }

  async function fetchUserProfile(userEmail, userUid) {
    const usersRef = collection(db, userCollectionName)
    if (userEmail) {
      const usersQuery = query(usersRef, where('email', '==', userEmail))
      const snapshot = await getDocs(usersQuery)
      if (!snapshot.empty) {
        const docSnapshot = snapshot.docs[0]
        return { id: docSnapshot.id, ...docSnapshot.data() }
      }
    }
    if (userUid) {
      const usersQuery = query(usersRef, where('userId', '==', userUid))
      const snapshot = await getDocs(usersQuery)
      if (!snapshot.empty) {
        const docSnapshot = snapshot.docs[0]
        return { id: docSnapshot.id, ...docSnapshot.data() }
      }
    }
    return null
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setIsLoading(true)
    try {
      const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence
      await setPersistence(auth, persistence)
      const credentials = await signInWithEmailAndPassword(auth, email.trim(), password)
      const profile = await fetchUserProfile(credentials.user?.email || email.trim(), credentials.user?.uid)
      if (!profile?.userType) {
        toast.error('No role found for this user. Please contact the administrator.')
        await signOut(auth)
        return
      }
      if (!allowedUserTypes.includes(profile.userType)) {
        toast.error('Your account is not authorized to access this system.')
        await signOut(auth)
        return
      }
      if (typeof window !== 'undefined') {
        localStorage.setItem('userType', profile.userType)
        if (profile.fullName) {
          localStorage.setItem('fullName', profile.fullName)
        }
        if (profile.userId) {
          localStorage.setItem('userId', profile.userId)
        }
      }
      toast.success('Welcome back!')
      navigate('/dashboard')
    } catch (error) {
      toast.error(getAuthErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-foreground">Login</h1>
        <p className="text-sm text-muted-foreground">
          Sign in to access the project management system
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <Label htmlFor="username" className="text-sm font-medium text-foreground">
            User Name
          </Label>
          <div className="relative">
            <Input
              id="username"
              type="email"
              placeholder="Enter your email address"
              className="h-11 rounded-md border border-[#d9ded8] bg-white pr-10 text-sm"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <User className="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="password" className="text-sm font-medium text-foreground">
            Password
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              className="h-11 rounded-md border border-[#d9ded8] bg-white pr-10 text-sm"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox
              id="remember"
              checked={rememberMe}
              onCheckedChange={(value) => setRememberMe(Boolean(value))}
            />
            <Label htmlFor="remember" className="text-xs text-muted-foreground">
              Remember me
            </Label>
          </div>
          <Link to="/reset-password" className="text-xs text-[#0b3d2b] hover:underline">
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          className="h-11 w-full bg-[#0b3d2b] text-white hover:bg-[#0a3425]"
          disabled={isLoading}
        >
          {isLoading ? 'Logging in...' : 'Login'}
        </Button>
      </form>
    </div>
  )
}
